import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@tds/core-box';
import DimpleDivider from '@tds/core-dimple-divider';
import Heading from '@tds/core-heading';
import Link from '@tds/core-link';
import { Download } from '@tds/core-interactive-icon';
import TextArea from '@tds/core-text-area';
import Button from '@tds/core-button';
import Select from '@tds/core-select';
import axios from 'axios';

import {
  SummaryWrapper,
  DefectAttachmentRow,
  DefectSummaryFirstCol,
  DefectSummaryRow,
  DefectSummarySecondCol,
  OrderDetailsBox
} from '../css';
import DefectSummaryHeader from './DefectSummaryHeader';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { FILTER_BY_STATUS_OPTIONS } from '../constants';

import { defectManagementConfig, getEnvironment } from '../../utils/request';

const DefectSummary = ({ setFetchData, openModal, defect }) => {
  const localizedString = useLocalizedStringsContext();
  const {
    DEFECT_MANAGEMENT: {
      DEFECT_SUMMARY = '',
      ATTACHMENTS = '',
      SUMMARY_TABLE = {},
      COMMENTS = '',
      ADD_COMMENT = '',
      ADD_BUTTON = '',
      FILTER_BY_STATUS = {},
      STATUS = '',
      CHANGE_STATUS = ''
    } = {}
  } = localizedString;
  const {
    defectWebform = '',
    updatedAt = '',
    priority = '',
    id = '',
    reportIssueAttachments = '',
    status: defectStatus,
    comments = ''
  } = defect || {};
  const comment = React.createRef();

  const updatedDefects = comments
    ? { ...defect }
    : { ...defect, comments: 'No comments' };

  const [updatedComment, setUpdatedComment] = useState('');
  const [status, setStatus] = useState('');

  const updatedDefectComment = () => {
    const { sof2ApiHost } = getEnvironment();
    const url = `${sof2ApiHost}/defects/updatecomments/${id}`;
    const body = {
      comments: updatedComment
    };
    const config = defectManagementConfig('post', url);
    axios({ ...config, data: body })
      .then(res => {
        const { data = {} } = res;
        if (data.success === 1) {
          openModal(false);
          setFetchData(true);
        }
      })
      .catch(err => new Error(err));
  };

  const updatedDefectStatus = () => {
    const { sof2ApiHost } = getEnvironment();
    const url = `${sof2ApiHost}/defects/updatestatus/${id}`;
    const body = {
      status
    };
    const config = defectManagementConfig('post', url);
    axios({ ...config, data: body })
      .then(res => {
        const { data = {} } = res;
        if (data.success === 1) {
          openModal(false);
          setFetchData(true);
        }
      })
      .catch(err => new Error(err));
  };

  return (
    <SummaryWrapper>
      <Box pb={24}>
        <OrderDetailsBox>
          <DefectSummaryHeader
            priority={priority}
            status={defectStatus}
            reportedOn={updatedAt}
            webForm={defectWebform}
          />
        </OrderDetailsBox>
        <DimpleDivider />
        <Box pb={12}>
          <Heading level="h2" tag="h4">
            {DEFECT_SUMMARY}
          </Heading>
          {updatedDefects &&
            SUMMARY_TABLE &&
            Object.keys(SUMMARY_TABLE).map(key => (
              <DefectSummaryRow>
                <div className="row">
                  <div className="col-4">
                    <DefectSummaryFirstCol>
                      {SUMMARY_TABLE[key]}
                    </DefectSummaryFirstCol>
                  </div>
                  <div className="col-8">
                    <DefectSummarySecondCol>
                      {updatedDefects[key]}
                    </DefectSummarySecondCol>
                  </div>
                </div>
              </DefectSummaryRow>
            ))}
        </Box>
        <DimpleDivider />
        <Box pb={24}>
          <Heading level="h2" tag="h4">
            {ATTACHMENTS}
          </Heading>
          <DimpleDivider />
          <DefectAttachmentRow>
            <div className="row">
              <div className="col-4">
                {reportIssueAttachments || 'No Attachments'}
              </div>
              {reportIssueAttachments && (
                <div style={{ textAlign: 'end' }} className="col-8">
                  <Link
                    href={reportIssueAttachments}
                    icon={Download}
                    iconPosition="left"
                  />
                </div>
              )}
            </div>
          </DefectAttachmentRow>
          <DimpleDivider />
        </Box>
        <Box pb={24}>
          <Heading level="h2" tag="h4">
            {COMMENTS}
          </Heading>
          <DimpleDivider />
          <TextArea
            label={ADD_COMMENT}
            ref={comment}
            onChange={e => {
              setUpdatedComment(e.target.value);
            }}
          />
          <div
            style={{
              textAlign: 'center',
              marginTop: '10px',
              marginBottom: '10px'
            }}
          >
            <Button key={1} variant="secondary" onClick={updatedDefectComment}>
              {ADD_BUTTON}
            </Button>
          </div>
          <DimpleDivider />
        </Box>
        <Box pb={24}>
          <Heading level="h2" tag="h4">
            {STATUS}
          </Heading>
          <DimpleDivider />
          <Select
            label={CHANGE_STATUS}
            placeholder={FILTER_BY_STATUS.placeholder}
            options={FILTER_BY_STATUS_OPTIONS.map(o => ({
              text: o,
              value: o
            }))}
            onChange={e => {
              setStatus(e.target.value);
            }}
          />
          <div
            style={{
              textAlign: 'center',
              marginTop: '10px',
              marginBottom: '10px'
            }}
          >
            <Button key={2} variant="secondary" onClick={updatedDefectStatus}>
              {ADD_BUTTON}
            </Button>
          </div>
          <DimpleDivider />
        </Box>
      </Box>
    </SummaryWrapper>
  );
};

DefectSummary.propTypes = {
  defect: PropTypes.objectOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
  setFetchData: PropTypes.func.isRequired
};

export default DefectSummary;
